export const DOCUMENT_SCHEMA_VERSION = 1;

const LEGAL_ADDRESSEE_PATTERN = /^(?:se(?:ñ|n)or(?:a)?|sres?\.?|excma?\.?|excmo\.?)\b.*:\s*$/i;
const LEGAL_SECTION_PATTERN = /^(?:[ivxlcdm]+|\d+)\s*(?:[.)-]|\.\s*-)/i;

export function isLegalHeaderBlock(value = "") {
  const lines = String(value)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length || lines.length > 4) return false;
  return lines.every((line) => {
    if (
      line.length > 220
      || line.endsWith(":")
      || LEGAL_ADDRESSEE_PATTERN.test(line)
      || LEGAL_SECTION_PATTERN.test(line)
    ) return false;
    const letters = line.match(/[a-z\u00c0-\u024f]/gi) || [];
    return letters.length >= 4
      && letters.join("") === letters.join("").toLocaleUpperCase("es-AR");
  });
}

export function generatedParagraphAttributes(value = "", {
  legalIndent = true,
  autoAlignHeader = true,
} = {}) {
  return {
    legalIndent: Boolean(legalIndent),
    ...(autoAlignHeader && isLegalHeaderBlock(value)
      ? { textAlign: "right" }
      : {}),
  };
}

function inlineContent(value = "") {
  const content = [];
  String(value).split("\n").forEach((line, index) => {
    if (index) content.push({ type: "hardBreak" });
    if (line) content.push({ type: "text", text: line });
  });
  return content;
}

export function generatedTextNodes(value = "", options = {}) {
  const paragraphs = String(value)
    .replace(/\r\n?/g, "\n")
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  let headerRegion = true;
  return paragraphs.map((text) => {
    const attributes = generatedParagraphAttributes(text, {
      ...options,
      autoAlignHeader: options.autoAlignHeader !== false && headerRegion,
    });
    if (attributes.textAlign !== "right") headerRegion = false;
    return {
      type: "paragraph",
      attrs: attributes,
      content: inlineContent(text),
    };
  });
}

export function emptyDocument({ legalIndent = true } = {}) {
  return {
    type: "doc",
    content: [{
      type: "paragraph",
      attrs: { legalIndent: Boolean(legalIndent) },
    }],
  };
}

export function plainTextToDocument(value = "", options = {}) {
  const paragraphs = generatedTextNodes(value, options);
  return {
    type: "doc",
    content: paragraphs.length ? paragraphs : emptyDocument(options).content,
  };
}

function textFromNode(node) {
  if (!node || typeof node !== "object") return "";
  if (node.type === "text") return typeof node.text === "string" ? node.text : "";
  if (node.type === "hardBreak") return "\n";
  const content = Array.isArray(node.content)
    ? node.content.map(textFromNode).join("")
    : "";
  if (["paragraph", "heading"].includes(node.type)) return `${content}\n`;
  return content;
}

export function documentToPlainText(document) {
  return textFromNode(document).replace(/\n{3,}/g, "\n\n").trim();
}

export function normalizeDocument(value) {
  if (
    value
    && typeof value === "object"
    && value.type === "doc"
    && Array.isArray(value.content)
  ) {
    return value;
  }
  if (typeof value === "string") return plainTextToDocument(value);
  return emptyDocument();
}
